import { useCallback } from 'react';
import { differenceInMinutes, isBefore, parseISO, subMinutes } from 'date-fns';

import { Analytics } from '@trello/atlassian-analytics';
import type { Traits } from '@trello/atlassian-personalization';
import {
  getUserTraits,
  TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD,
  TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_EXPERIENCE,
  TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_FIRST_VIEWED,
} from '@trello/atlassian-personalization';
import { useAaId } from '@trello/authentication';
import { TrelloStorage } from '@trello/storage';

import type { AdTouchpointSourceType } from './TouchpointSourceType';

interface AdSequencerParams {
  experience: string;
  adTouchpointSources: AdTouchpointSourceType[];
  adRotationIntervalInMinutes?: number;
}

interface LastAdSeenAttributes {
  lastAdSeenExperience: string | undefined;
  lastAdSeen: AdTouchpointSourceType | undefined;
  lastAdSeenTimestamp: Date | undefined;
}

const extractLastSeenAdAttributesFromTraits = (
  traits: Traits,
): LastAdSeenAttributes => {
  return {
    lastAdSeenExperience: traits?.[
      TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_EXPERIENCE
    ]
      ? String(traits[TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_EXPERIENCE])
      : undefined,
    lastAdSeen: traits?.[
      TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD
    ] as AdTouchpointSourceType,
    lastAdSeenTimestamp: traits?.[
      TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_FIRST_VIEWED
    ]
      ? parseISO(
          String(
            traits[TRELLO_UI_DISCOVERY_AD_DISPLAYED_LATEST_AD_FIRST_VIEWED],
          ),
        )
      : undefined,
  };
};

const LAST_AD_SEEN_ATTRIBUTES_CACHE_KEY_PREFIX = 'lastAdSeenAttributes';

const getLastAdSeenAttributesCacheKey = (aaId: string) => {
  return `${LAST_AD_SEEN_ATTRIBUTES_CACHE_KEY_PREFIX}_${aaId}`;
};

const DAY_IN_MINUTES = 60 * 24;

/**
 * Ad Sequencer hook.
 *
 * Suggests the next ad for the user given the last ad they saw and when they
 * first saw it.
 *
 * @param experience the ad experience that the user was enrolled in (currently this is the experiment)
 * @param adTouchpointSources an array containing the ads for the specified experience
 * @param adRotationIntervalInMinutes the number of minutes between each ad rotation
 * @returns {Object} [obj] exported members
 * @returns {Promise<String>} [obj.getNextAd] returns the next ad
 */
export const useAdSequencer = ({
  experience,
  adTouchpointSources,
  adRotationIntervalInMinutes = 2 * DAY_IN_MINUTES,
}: AdSequencerParams) => {
  const aaId = useAaId();

  const getNextAd = useCallback(async () => {
    if (!aaId) {
      return;
    }

    const today = new Date();
    const adRotationIntervalMinutesAgo = subMinutes(
      today,
      adRotationIntervalInMinutes,
    );

    let lastAdSeenAttributes: LastAdSeenAttributes | undefined = undefined;
    const lastAdSeenAttributesCacheKey = getLastAdSeenAttributesCacheKey(aaId);
    const cachedAttributes = TrelloStorage.get(lastAdSeenAttributesCacheKey);
    if (cachedAttributes) {
      if (Object.keys(cachedAttributes).length === 0) {
        // users shouldn't get into this state but unset the cache if we've
        // accidentally saved an empty attributes object.
        TrelloStorage.unset(lastAdSeenAttributesCacheKey);
      } else {
        const lastAdSeenTimestamp = parseISO(
          cachedAttributes.lastAdSeenTimestamp,
        );

        const cacheExpired = isBefore(
          lastAdSeenTimestamp,
          adRotationIntervalMinutesAgo,
        );

        if (!cacheExpired) {
          lastAdSeenAttributes = { ...cachedAttributes, lastAdSeenTimestamp };
        }
      }
    }

    if (!lastAdSeenAttributes) {
      try {
        const traits = await getUserTraits(aaId);
        lastAdSeenAttributes = extractLastSeenAdAttributesFromTraits(traits);

        if (
          lastAdSeenAttributes.lastAdSeen &&
          lastAdSeenAttributes.lastAdSeenExperience &&
          lastAdSeenAttributes.lastAdSeenTimestamp
        ) {
          TrelloStorage.set(lastAdSeenAttributesCacheKey, lastAdSeenAttributes);
        }
      } catch (e) {
        Analytics.sendOperationalEvent({
          action: 'failed',
          actionSubject: 'getNextAd',
          source: '@trello/ad-sequencer',
          attributes: {
            errorMessage: (e as Error).message,
          },
        });

        return undefined;
      }
    }

    if (lastAdSeenAttributes) {
      const { lastAdSeenExperience, lastAdSeen, lastAdSeenTimestamp } =
        lastAdSeenAttributes;

      const userHasSeenAds =
        lastAdSeenExperience && lastAdSeen && lastAdSeenTimestamp;

      const firstAdOfEnrolledExperience = adTouchpointSources.at(0);

      if (!userHasSeenAds) {
        Analytics.sendOperationalEvent({
          action: 'succeeded',
          actionSubject: 'getNextAd',
          source: '@trello/ad-sequencer',
          attributes: {
            lastAdSeen: undefined,
            lastAdSeenExperience: undefined,
            lastAdSeenTimestamp: undefined,
            minutesSinceLastAdImpression: 0,
            experience,
            suggestedNextAd: firstAdOfEnrolledExperience,
          },
        });

        // Return first ad in the experience if the user has never seen any ads
        return firstAdOfEnrolledExperience;
      }

      const minutesSinceLastAdImpression = differenceInMinutes(
        today,
        lastAdSeenTimestamp,
      );

      if (lastAdSeenExperience !== experience) {
        // Reset to the first ad in the new experience if the user has been enrolled in a new experience.
        // Worse case the user may see the same ad for longer than the specified ad rotation interval.

        Analytics.sendOperationalEvent({
          action: 'succeeded',
          actionSubject: 'getNextAd',
          source: '@trello/ad-sequencer',
          attributes: {
            lastAdSeen,
            lastAdSeenExperience,
            lastAdSeenTimestamp: lastAdSeenTimestamp.toISOString(),
            minutesSinceLastAdImpression,
            experience,
            suggestedNextAd: firstAdOfEnrolledExperience,
          },
        });

        return firstAdOfEnrolledExperience;
      }

      const adRotationIntervalLapsed = isBefore(
        lastAdSeenTimestamp,
        adRotationIntervalMinutesAgo,
      );

      if (adRotationIntervalLapsed) {
        const adTouchpointSourceIndex = adTouchpointSources.indexOf(lastAdSeen);

        const elapsedAdRotationPeriods = Math.floor(
          minutesSinceLastAdImpression / adRotationIntervalInMinutes,
        );

        // rotate through to the next ad that the user should see (skipping ads
        // that they should have seen in the sequence within the rotation interval)
        const nextAd =
          adTouchpointSources[
            (adTouchpointSourceIndex + elapsedAdRotationPeriods) %
              adTouchpointSources.length
          ];

        Analytics.sendOperationalEvent({
          action: 'succeeded',
          actionSubject: 'getNextAd',
          source: '@trello/ad-sequencer',
          attributes: {
            lastAdSeen,
            lastAdSeenExperience,
            lastAdSeenTimestamp: lastAdSeenTimestamp.toISOString(),
            minutesSinceLastAdImpression,
            experience,
            suggestedNextAd: nextAd,
          },
        });

        return nextAd;
      }

      Analytics.sendOperationalEvent({
        action: 'succeeded',
        actionSubject: 'getNextAd',
        source: '@trello/ad-sequencer',
        attributes: {
          lastAdSeen,
          lastAdSeenExperience,
          lastAdSeenTimestamp: lastAdSeenTimestamp.toISOString(),
          minutesSinceLastAdImpression,
          experience,
          suggestedNextAd: lastAdSeen,
        },
      });

      return lastAdSeen;
    }
  }, [aaId, adRotationIntervalInMinutes, experience, adTouchpointSources]);

  return {
    getNextAd,
  };
};
